import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { SubscriptionPlanCat, SUBSCRIPTION_PLAN_TEXT } from '@cmms/models';

@customElement('upgrade-placeholder')
export class UpgradePlaceholder extends LitElement {
  @property({ type: String })
  featureName = '';

  @property({ type: String })
  description = '';

  @property({ type: String })
  icon = 'star';

  @property({ type: Array })
  benefits: string[] = [];

  @property({ type: Number })
  currentPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;

  static styles = css`
    :host {
      display: block;
      padding: 2rem;
      text-align: center;
    }

    .placeholder-container {
      max-width: 600px;
      margin: 0 auto;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 16px;
      padding: 3rem 2rem;
      color: white;
      box-shadow: 0 20px 40px rgba(102, 126, 234, 0.3);
    }

    .icon {
      font-size: 4rem;
      margin-bottom: 1rem;
      opacity: 0.9;
    }

    .feature-name {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    .description {
      font-size: 1.1rem;
      margin-bottom: 2rem;
      opacity: 0.9;
      line-height: 1.6;
    }

    .benefits {
      list-style: none;
      padding: 0;
      margin: 2rem 0;
    }

    .benefit-item {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0.75rem;
      font-size: 1rem;
    }

    .benefit-item sl-icon {
      margin-right: 0.5rem;
      color: #4ade80;
    }

    .upgrade-button {
      background: white;
      color: #667eea;
      border: none;
      padding: 1rem 2rem;
      border-radius: 8px;
      font-size: 1.1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .upgrade-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    }

    .current-plan {
      background: rgba(255, 255, 255, 0.2);
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.9rem;
      margin-bottom: 1rem;
      display: inline-block;
    }

    @media (max-width: 768px) {
      .placeholder-container {
        padding: 2rem 1rem;
      }
      
      .feature-name {
        font-size: 1.5rem;
      }
      
      .description {
        font-size: 1rem;
      }
    }
  `;

  private handleUpgradeClick() {
    // Dispatch event for parent to handle
    this.dispatchEvent(new CustomEvent('upgrade-requested', {
      detail: { feature: this.featureName },
      bubbles: true,
      composed: true
    }));
  }

  private getCurrentPlanText(): string {
    return SUBSCRIPTION_PLAN_TEXT[this.currentPlan] || 'Unknown';
  }

  private getUpgradeTargetText(): string {
    // Determine what plan to upgrade to based on current plan
    switch (this.currentPlan) {
      case SubscriptionPlanCat.STARTER:
        return 'Professional';
      case SubscriptionPlanCat.PROFESSIONAL:
        return 'Enterprise';
      case SubscriptionPlanCat.ENTERPRISE:
        return 'Contact Sales'; // Already on highest plan
      default:
        return 'Professional';
    }
  }

  render() {
    const currentPlanText = this.getCurrentPlanText();
    const upgradeTargetText = this.getUpgradeTargetText();
    
    return html`
      <div class="placeholder-container">
        <div class="current-plan">Current Plan: ${currentPlanText}</div>
        
        <sl-icon name="${this.icon}" class="icon"></sl-icon>
        
        <h2 class="feature-name">${this.featureName}</h2>
        <p class="description">${this.description}</p>
        
        ${this.benefits.length > 0 ? html`
          <ul class="benefits">
            ${this.benefits.map(benefit => html`
              <li class="benefit-item">
                <sl-icon name="check-circle-fill"></sl-icon>
                ${benefit}
              </li>
            `)}
          </ul>
        ` : ''}
        
        <button class="upgrade-button" @click=${this.handleUpgradeClick}>
          ${this.currentPlan === SubscriptionPlanCat.ENTERPRISE 
            ? 'Contact Sales' 
            : `Upgrade to ${upgradeTargetText}`
          }
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'upgrade-placeholder': UpgradePlaceholder;
  }
}
